// Document state for the visual builder.
//
// Deliberately plain JS with pub/sub rather than a Stimulus controller: every
// pane needs the same document, and keeping mutation in one place means the
// controllers stay presentational.

const UNDO_DEPTH = 50

export default class DocumentStore {
  constructor(document, lockVersion) {
    this.doc = document
    this.lockVersion = lockVersion
    this.selectedId = null
    this.breakpoint = "base"
    this.undoStack = []
    this.redoStack = []
    this.listeners = new Set()
  }

  get document() { return this.doc }
  get root() { return this.doc.root }
  get canUndo() { return this.undoStack.length > 0 }
  get canRedo() { return this.redoStack.length > 0 }
  get selected() { return this.selectedId ? this.node(this.selectedId) : null }

  subscribe(fn) {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  emit(event) {
    this.listeners.forEach((fn) => fn(event))
  }

  node(id, current = this.doc.root) {
    if (!current) return null
    if (current.id === id) return current
    for (const child of current.children || []) {
      const found = this.node(id, child)
      if (found) return found
    }
    return null
  }

  parentOf(id, current = this.doc.root) {
    for (const child of current.children || []) {
      if (child.id === id) return current
      const found = this.parentOf(id, child)
      if (found) return found
    }
    return null
  }

  indexOf(id) {
    const parent = this.parentOf(id)
    return parent ? parent.children.findIndex((child) => child.id === id) : -1
  }

  select(id) {
    this.selectedId = id
    this.emit({ type: "select", id })
  }

  setBreakpoint(breakpoint) {
    this.breakpoint = breakpoint
    this.emit({ type: "breakpoint", breakpoint })
  }

  // Snapshots rather than patches: documents are small, and patch-based undo
  // is expensive to get right across structural operations.
  snapshot() {
    this.undoStack.push(JSON.stringify(this.doc))
    if (this.undoStack.length > UNDO_DEPTH) this.undoStack.shift()
    this.redoStack.length = 0
  }

  insert(node, parentId, index) {
    const parent = this.node(parentId)
    if (!parent) return false

    this.snapshot()
    parent.children = parent.children || []
    const at = index === undefined || index === null ? parent.children.length : index
    parent.children.splice(at, 0, node)
    this.emit({ type: "structure", action: "insert", node, id: node.id, parentId, index: at })
    return true
  }

  remove(id) {
    const parent = this.parentOf(id)
    if (!parent) return false

    this.snapshot()
    parent.children = parent.children.filter((child) => child.id !== id)
    if (this.selectedId === id) this.selectedId = null
    this.emit({ type: "structure", action: "remove", id })
    return true
  }

  move(id, parentId, index) {
    const node = this.node(id)
    const parent = this.parentOf(id)
    const target = this.node(parentId)
    if (!node || !parent || !target) return false
    // A node can never be moved into its own subtree.
    if (this.contains(node, parentId)) return false

    this.snapshot()
    const from = parent.children.findIndex((child) => child.id === id)
    parent.children.splice(from, 1)
    target.children = target.children || []
    let at = index === undefined || index === null ? target.children.length : index
    if (parent === target && from < at) at -= 1
    target.children.splice(at, 0, node)
    this.emit({ type: "structure", action: "move", id, parentId, index: at })
    return true
  }

  // Moving one place is expressed as a swap rather than through move(), whose
  // index is relative to the list *after* removal -- passing index + 1 there
  // lands the node back where it started.
  nudge(id, delta) {
    const parent = this.parentOf(id)
    if (!parent) return false

    const from = parent.children.findIndex((child) => child.id === id)
    const to = from + delta
    if (to < 0 || to >= parent.children.length) return false

    this.snapshot()
    const [node] = parent.children.splice(from, 1)
    parent.children.splice(to, 0, node)
    this.emit({ type: "structure", action: "move", id, parentId: parent.id, index: to })
    return true
  }

  contains(node, id) {
    if (node.id === id) return true
    return (node.children || []).some((child) => this.contains(child, id))
  }

  setStyle(id, key, value) {
    const node = this.node(id)
    if (!node) return

    node.style = node.style || {}
    const bucket = (node.style[this.breakpoint] = node.style[this.breakpoint] || {})
    if (value === null || value === "") delete bucket[key]
    else bucket[key] = value
    if (Object.keys(bucket).length === 0) delete node.style[this.breakpoint]

    this.emit({ type: "style", id, key, value, breakpoint: this.breakpoint })
  }

  // The value set at the current breakpoint, ignoring inherited ones, so the
  // inspector can distinguish "set here" from "inherited from base".
  styleValue(id, key) {
    return this.node(id)?.style?.[this.breakpoint]?.[key] ?? null
  }

  // What the element actually resolves to, walking the cascade base -> md -> lg.
  effectiveStyle(id, key) {
    const style = this.node(id)?.style || {}
    const order = ["base", "md", "lg"]
    let value = null
    for (const bp of order.slice(0, order.indexOf(this.breakpoint) + 1)) {
      if (style[bp] && style[bp][key] !== undefined) value = style[bp][key]
    }
    return value
  }

  setProp(id, key, value) {
    const node = this.node(id)
    if (!node) return

    node.props = node.props || {}
    node.props[key] = value
    this.emit({ type: "prop", id, key, value })
  }

  prop(id, key) {
    return this.node(id)?.props?.[key] ?? null
  }

  replaceDocument(doc) {
    this.doc = doc
    this.selectedId = null
    this.emit({ type: "document" })
  }

  undo() {
    if (!this.canUndo) return false
    this.redoStack.push(JSON.stringify(this.doc))
    this.doc = JSON.parse(this.undoStack.pop())
    this.selectedId = null
    this.emit({ type: "document" })
    return true
  }

  redo() {
    if (!this.canRedo) return false
    this.undoStack.push(JSON.stringify(this.doc))
    this.doc = JSON.parse(this.redoStack.pop())
    this.selectedId = null
    this.emit({ type: "document" })
    return true
  }
}
